import React, { useState } from "react";
import DashboardLayout from "../layouts/Dashboardlayout";
import bg from "../../src/assets/svg/bg.svg";
import gradient from "../../src/assets/svg/gradient.svg";
import gradinet1 from "../../src/assets/svg/gradient1.svg";
import { AnimatedRobot } from "../components/ui/loader/AnimatedRobot";
import { ColorPicker } from "../components/ColorPicker";
import { FeatureSelector } from "../components/FeatureSelector";
import { Textarea } from "../components/ui/loader/TextArea";
import OpenAI, { Configuration, OpenAIApi } from "openai";
import { toast } from "sonner";

export default function BuildSiteAI() {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [siteConfig, setSiteConfig] = useState({
    businessName: "",
    businessType: "",
    style: "",
    features: [],
    colorScheme: null,
    content: "",
  });

  const openAi = new OpenAI({
    apiKey: process.env.REACT_APP_OPENAI_API_KEY,
    dangerouslyAllowBrowser: true,
  });

  const businessTypes = [
    { id: "business", name: "Business Website" },
    { id: "ecommerce", name: "E-Commerce" },
    { id: "portfolio", name: "Portfolio" },
    { id: "blog", name: "Blog" },
    { id: "landing", name: "Landing Page" },
  ];

  const generateWebsite = async () => {
    try {
      setLoading(true);

      const generateImages = async (imagePrompts) => {
        const images = [];
        for (const prompt of imagePrompts) {
          const response = await openAi.images.generate({
            model: "dall-e-3",
            prompt: prompt,
            n: 1,
            size: "1024x1024",
          });
          images.push(response.data[0].url);
        }
        return images;
      };

      const generateStructure = async () => {
        const response = await openAi.chat.completions.create({
          model: "gpt-4",
          messages: [
            {
              role: "system",
              content:
                "You are a professional web developer who creates modern, responsive websites with a focus on user experience and visual appeal.",
            },
            {
              role: "user",
              content: `Create a detailed website structure for ${
                siteConfig.businessName
              }, a ${siteConfig.businessType} business.
                Features needed: ${siteConfig.features.join(", ")}
                Color scheme: Primary ${
                  siteConfig.colorScheme?.primary
                }, Secondary ${siteConfig.colorScheme?.secondary}
                Additional requirements: ${siteConfig.content}
                
                Also include:
                - Image requirements (list of image descriptions for AI generation)
                - Content sections with detailed text
                - Call-to-action buttons
                - Contact forms
                - Social media integration
                - Analytics setup
                
                Provide the response as a JSON structure with these properties:
                - components (array of sections)
                - seoMetadata (title, description, keywords)
                - navigationItems
                - imagePrompts (array of image generation prompts)
                - socialMedia (social media links and integration)
                - analytics (tracking IDs and setup)
                Each component should include: id, name, content, styling, and imageRequirement if needed.`,
            },
          ],
          temperature: 0.7,
          max_tokens: 2500,
        });

        return JSON.parse(response.choices[0].message.content);
      };

      const generateHTML = async (structure, generatedImages) => {
        const response = await openAi.chat.completions.create({
          model: "gpt-4",
          messages: [
            {
              role: "system",
              content:
                "You are a professional web developer who writes clean, semantic HTML and modern CSS.",
            },
            {
              role: "user",
              content: `Generate a complete HTML website with internal CSS based on this structure:
                ${JSON.stringify({ ...structure, generatedImages })}
                
                Requirements:
                - Use semantic HTML5 elements
                - Include responsive design (mobile-first)
                - Use CSS variables for colors: ${
                  siteConfig.colorScheme?.primary
                }, ${siteConfig.colorScheme?.secondary}
                - Include modern CSS features (flexbox/grid)
                - Add smooth animations for interactions
                - Ensure accessibility standards
                - Include meta tags for SEO
                - Add social media integration
                - Include analytics tracking code
                - Add cookie consent
                - Implement contact forms
                - Add image lazy loading
                - Implement responsive images
                
                Return complete HTML file with internal CSS.`,
            },
          ],
          temperature: 0.7,
          max_tokens: 4000,
        });

        return response.choices[0].message.content;
      };

      const generateJS = async (structure) => {
        const response = await openAi.chat.completions.create({
          model: "gpt-4",
          messages: [
            {
              role: "system",
              content:
                "You are a professional JavaScript developer who writes clean, modern JS code.",
            },
            {
              role: "user",
              content: `Create JavaScript code for website interactivity:
                - Smooth scrolling navigation
                - Form validation and submission
                - Mobile menu toggle
                - Image gallery with lightbox
                - Loading animations
                - Cookie consent handling
                - Analytics integration
                - Social media widgets
                - Contact form handling
                - Dynamic content loading
                - Lazy loading images
                - Performance optimization
                
                Structure: ${JSON.stringify(structure)}
                
                Return clean JavaScript code that can be added to the website.`,
            },
          ],
          temperature: 0.7,
          max_tokens: 2500,
        });

        return response.choices[0].message.content;
      };

      // Execute all API calls in sequence
      const structure = await generateStructure();
      const generatedImages = await generateImages(structure.imagePrompts);
      const htmlContent = await generateHTML(structure, generatedImages);
      const jsContent = await generateJS(structure);

      setResults({
        components: structure.components,
        preview: htmlContent,
        javascript: jsContent,
        seoMetadata: structure.seoMetadata,
        navigationItems: structure.navigationItems,
        images: generatedImages,
        socialMedia: structure.socialMedia,
        analytics: structure.analytics,
      });
    } catch (error) {
      console.error("OpenAI API Error:", error);
      alert("Failed to generate website. Please check console for details.");
    } finally {
      setLoading(false);
    }
  };

  const renderConfigForm = () => (
    <div className="w-full md:w-[907px] bg-[#121314] rounded-[11.264px] p-[22px] flex flex-col gap-[22px]">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-4">
          <div>
            <label className="text-white text-sm">Business Name</label>
            <input
              type="text"
              className="w-full bg-[#ffffff0a] rounded-lg p-2 text-white"
              value={siteConfig.businessName}
              onChange={(e) =>
                setSiteConfig((prev) => ({
                  ...prev,
                  businessName: e.target.value,
                }))
              }
              placeholder="Enter your business name"
            />
          </div>

          <div>
            <label className="text-white text-sm">Business Type</label>
            <select
              className="w-full bg-[#ffffff0a] rounded-lg p-2 text-white [&>option]:bg-[#121314]"
              value={siteConfig.businessType}
              onChange={(e) =>
                setSiteConfig((prev) => ({
                  ...prev,
                  businessType: e.target.value,
                }))
              }
            >
              <option value="">Select type</option>
              {businessTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </select>
          </div>

          <ColorPicker
            value={siteConfig.colorScheme}
            onChange={(color) =>
              setSiteConfig((prev) => ({
                ...prev,
                colorScheme: color,
              }))
            }
          />
        </div>

        {/* Right Column */}
        <div className="space-y-4">
          <label className="text-white text-sm">Select Features</label>
          <FeatureSelector
            selected={siteConfig.features}
            onChange={(features) =>
              setSiteConfig((prev) => ({
                ...prev,
                features,
              }))
            }
          />
        </div>
      </div>

      <div>
        <label className="text-white text-sm">Additional Details</label>
        <Textarea
          placeholder="Describe your business and any specific requirements..."
          value={siteConfig.content}
          onChange={(e) =>
            setSiteConfig((prev) => ({
              ...prev,
              content: e.target.value,
            }))
          }
          className="w-full bg-[#ffffff0a] rounded-lg p-2 text-white min-h-[100px]"
        />
      </div>

      <button
        onClick={() => {
          toast.success("Coming Soon !!");
        }}
        className="w-full h-[40px] btnn rounded-[9px] text-white text-sm disabled:opacity-50"
      >
        Generate Website
      </button>
    </div>
  );

  const renderResults = () => (
    <div className="w-full h-full p-4 md:p-10 flex flex-col md:flex-row gap-4 md:gap-10">
      <div className="w-full md:w-[30%] bg-[#121314] rounded-lg p-4 md:p-6">
        <h3 className="text-white text-xl mb-4">Generated Components</h3>
        <div className="space-y-4">
          <div className="space-y-2">
            {results.components.map((component, index) => (
              <div
                key={index}
                className="p-3 bg-[#ffffff0a] rounded-lg text-white cursor-pointer hover:bg-[#ffffff15]"
              >
                {component.name}
              </div>
            ))}
          </div>

          <h3 className="text-white text-xl mt-6 mb-4">Generated Images</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {results.images.map((image, index) => (
              <img
                key={index}
                src={image}
                alt={`Generated image ${index + 1}`}
                className="w-full rounded-lg"
              />
            ))}
          </div>
        </div>
      </div>

      <div className="w-full md:w-[70%] bg-[#121314] rounded-lg p-4 md:p-6">
        <h3 className="text-white text-xl mb-4">Preview</h3>
        <div className="bg-white rounded-lg w-full h-[500px] md:h-full overflow-hidden">
          <iframe
            srcDoc={results.preview}
            className="w-full h-full border-0"
            title="Website Preview"
          />
        </div>
      </div>
    </div>
  );

  return (
    <DashboardLayout>
      <div
        className="min-h-screen w-full bg-cover bg-center flex flex-col justify-center items-center relative px-4 pt-8 md:pt-0"
        style={{ backgroundImage: `url(${bg})` }}
      >
        <img
          className="absolute right-0 top-0 hidden md:block"
          src={gradient}
          alt="gradient"
        />

        {loading ? (
          <div className="flex flex-col items-center text-white gap-6">
            <AnimatedRobot className="w-full max-w-[481px] h-[280px]" />
            <p className="text-lg text-[#F2F4F7] font-medium text-center">
              Building your dream website... 🚀
              <br />
              This will just take a moment!
            </p>
          </div>
        ) : results ? (
          renderResults()
        ) : (
          <div className="md:mt-28 mt-[64px] md:pt-0 pt-[72px]">
            <div className="flex flex-col gap-4 text-center">
              <h2 className="md:text-[48px] text-[36px] font-semibold bg md:leading-[60px] leading-[40px]">
                {" "}
                AI Website Builder
              </h2>
              <p className="text-[#F2F4F7]">
                Create your professional website in minutes with AI
              </p>
            </div>

            <div className="relative mt-8">
              <div className="w-full md:w-[936px] bgh p-4 mx-auto">
                <div className="bgr w-full rounded-[12px]">
                  {renderConfigForm()}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
